import { DiscrepancyPopup } from "@/components/discrepancies/data-discrepancies/DiscrepancyPopup";
import { Form } from "@/components/shared/forms/Form";
import { useDiscrepanciesPage } from "@/components/discrepancies/data-discrepancies/hooks/useDiscrepanciesPage";
import { useRecordPermissions } from "@/components/discrepancies/data-discrepancies/hooks/useRecordPermissions";
import { CHLDAddEditForm } from "@/components/records/child/CHLDAddEditForm";
import CLENAddEditForm from "@/components/records/child/CLENAddEditForm";
import { ENRLAddEditForm } from "@/components/records/child/ENRLAddEditForm";
import { FAMIAddEditForm } from "@/components/records/child/FAMIAddEditForm";
import { LANGAddEditForm } from "@/components/records/child/LANGAddEditForm";
import { SLNGAddEditForm } from "@/components/records/child/SLNGAddEditForm";
import { SASSAddEditForm } from "@/components/records/staff/SASSAddEditForm";
import SCLRAddEditForm from "@/components/records/staff/SCLRAddEditForm";
import { STFFAddEditForm } from "@/components/records/staff/STFFAddEditForm";
import Card from "@/components/shared/Card";
import CategoryFilterCell from "@/components/shared/tables/CategoryFilterCell";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { stripAriaSelected } from "@/components/shared/tables/KendoTableClientState/kendo-cell-utils";
import { recordTypes } from "@/models";
import { type GridColumnProps } from "@progress/kendo-react-grid";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiEdit,
  FiEye,
  FiSave,
} from "react-icons/fi";
import Popup from "@/components/shared/Popup";
import { focusFirstInvalidField } from "@/utils/forms";
import { CLRMAddEditForm } from "@/components/records/classroom/CLRMAddEditForm";
import {
  CurrentView,
  EditDialogState,
} from "@/components/discrepancies/data-discrepancies/hooks/useDiscrepanciesPage";

const SORTED_RECORD_TYPES = [...recordTypes].sort();

const columns: GridColumnProps[] = [
  {
    id: "RecordID",
    field: "RecordID",
    title: "Record ID",
    cells: {
      data: (props) => (
        <td {...stripAriaSelected(props.tdProps)}>
          {props.dataItem.RecordID}
        </td>
      ),
    },
  },
  {
    id: "ReferenceRecordType",
    field: "ReferenceRecordType",
    title: "Record Type",
    cells: {
      filterCell: (props) => CategoryFilterCell(props, SORTED_RECORD_TYPES),
    },
  },
  { id: "ErrorCode", field: "ErrorCode", title: "Error Code" },
  { id: "ErrorMessage", field: "ErrorMessage", title: "Error Message" },
];

export function DiscrepanciesTab() {
  const {
    currentView,
    selectedDiscrepancy,
    editDialog,
    coreData,
    tableData,
    error,
    forms,
    returnToTab,
    isSubmitting,
    anomalyRun,
    handleRowClick,
    handleCloseAll,
    handleUpdateRecord,
    handleBackToDiscrepancy,
    handleSave,
    getRecordLabel,
  } = useDiscrepanciesPage();

  const { canViewRecord, canEditRecord } = useRecordPermissions();

  const filteredTableData = tableData.filter(
    (row) =>
      !row.ErrorCode?.startsWith("SSID") &&
      (row.ReferenceRecordType !== "ATTN" || canViewRecord("ATTN"))
  );

  const tableKey = filteredTableData.map((row) => row.RecordID).join(",");

  return (
    <>
      <Card className="mb-3 no-padding card-border">
        <div
          className="text-white p-3 d-flex align-items-center gap-2"
          style={{
            background: "var(--color-p3)",
            fontWeight: 500,
            borderRadius: "0.375rem 0.375rem 0px 0px",
          }}
        >
          <FiAlertCircle size={18} />
          <span>Errors By Record</span>
        </div>
        <KendoTableClientState
          key={tableKey}
          data={filteredTableData}
          columns={columns}
          error={error}
          onRowClick={handleRowClick}
        />
      </Card>

      {currentView === "discrepancy" && selectedDiscrepancy && (
        <DiscrepancyPopup
          key={`discrepancy-popup-${returnToTab}`}
          discrepancy={selectedDiscrepancy}
          onClose={handleCloseAll}
          errorMessageMap={coreData.errorMessageMap}
          onUpdateRecord={handleUpdateRecord}
          initialTab={returnToTab}
          anomalyRunEndTime={anomalyRun?.EndTime}
        />
      )}

      <EditDialog
        type="CHLD"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.chld}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("CHLD")}
      >
        <CHLDAddEditForm
          selectedChild={
            editDialog?.type === "CHLD" ? editDialog.data : undefined
          }
          form={forms.chld}
        />
      </EditDialog>

      <EditDialog
        type="ENRL"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.enrl}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("ENRL")}
      >
        <ENRLAddEditForm
          selectedEnrollment={
            editDialog?.type === "ENRL" ? editDialog.data : undefined
          }
          form={forms.enrl}
        />
      </EditDialog>

      <EditDialog
        type="CLEN"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.clen}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("CLEN")}
      >
        <CLENAddEditForm
          selectedCLEN={
            editDialog?.type === "CLEN" ? editDialog.data : undefined
          }
          form={forms.clen}
          clenDefaults={undefined}
        />
      </EditDialog>

      <EditDialog
        type="FAMI"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.fami}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("FAMI")}
      >
        <FAMIAddEditForm
          selectedFamily={
            editDialog?.type === "FAMI" ? editDialog.data : undefined
          }
          form={forms.fami}
        />
      </EditDialog>

      <EditDialog
        type="LANG"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.lang}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("LANG")}
      >
        <LANGAddEditForm
          selectedLang={
            editDialog?.type === "LANG" ? editDialog.data : undefined
          }
          form={forms.lang}
        />
      </EditDialog>

      <EditDialog
        type="SLNG"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.slng}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("SLNG")}
      >
        <SLNGAddEditForm
          selectedSLNG={
            editDialog?.type === "SLNG" ? editDialog.data : undefined
          }
          form={forms.slng}
          isOpen={true}
        />
      </EditDialog>

      <EditDialog
        type="STFF"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.stff}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("STFF")}
      >
        <STFFAddEditForm
          selectedStaff={
            editDialog?.type === "STFF" ? editDialog.data : undefined
          }
          form={forms.stff}
        />
      </EditDialog>

      <EditDialog
        type="SASS"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.sass}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("SASS")}
      >
        <SASSAddEditForm
          selectedStaffAssignment={
            editDialog?.type === "SASS" ? editDialog.data : undefined
          }
          form={forms.sass}
        />
      </EditDialog>

      <EditDialog
        type="SCLR"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.sclr}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("SCLR")}
      >
        <SCLRAddEditForm
          staffClassroom={
            editDialog?.type === "SCLR" ? editDialog.data : undefined
          }
          form={forms.sclr}
          sclrDefaults={undefined}
        />
      </EditDialog>

      <EditDialog
        type="CLRM"
        editDialog={editDialog}
        currentView={currentView}
        form={forms.clrm}
        onClose={handleCloseAll}
        onBack={handleBackToDiscrepancy}
        onSave={handleSave}
        getRecordLabel={getRecordLabel}
        isSubmitting={isSubmitting}
        canEdit={canEditRecord("CLRM")}
      >
        <CLRMAddEditForm
          key={
            editDialog?.type === "CLRM"
              ? ((editDialog.data as any)?.RecordId ?? "new")
              : "clrm"
          }
          selectedClassroom={
            editDialog?.type === "CLRM" ? editDialog.data : undefined
          }
          form={forms.clrm}
        />
      </EditDialog>
    </>
  );
}

type EditDialogProps = {
  type: string;
  editDialog: EditDialogState;
  currentView: CurrentView;
  form: any;
  onClose: () => void;
  onBack: () => void;
  onSave: (data: any) => void;
  getRecordLabel: (type: string) => string;
  isSubmitting: boolean;
  canEdit: boolean;
  children: React.ReactNode;
};

function EditDialog({
  type,
  editDialog,
  currentView,
  form,
  onClose,
  onBack,
  onSave,
  getRecordLabel,
  isSubmitting,
  canEdit,
  children,
}: EditDialogProps) {
  if (currentView !== "edit" || !editDialog || editDialog.type !== type) {
    return null;
  }

  const formId = `${type.toLowerCase()}-edit-form`;

  return (
    <Popup
      show={true}
      header={
        <div
          className="d-flex align-items-center gap-2"
          style={{ fontSize: "1.25rem" }}
        >
          {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
          <span>
            {canEdit ? "Edit" : "View"} {getRecordLabel(type)} Record
          </span>
        </div>
      }
      size="lg"
      onClose={onClose}
      hasButton={false}
      submitFormId={formId}
      submitButtonText={
        <div className="d-flex align-items-center gap-2">
          <FiSave size={16} />
          <span>Save</span>
        </div>
      }
      submitButtonLoading={isSubmitting}
      submitButtonLoadingText="Saving..."
      secondButtonText="Cancel"
      onSecondButtonClick={onClose}
      backButtonText={
        <div className="d-flex align-items-center gap-2">
          <FiArrowLeft size={16} />
          <span>Back</span>
        </div>
      }
      onBackButtonClick={onBack}
      canEdit={canEdit}
    >
      <Form
        id={formId}
        onSubmit={form.handleSubmit(onSave, () =>
          focusFirstInvalidField(`#${formId}`)
        )}
      >
        {children}
      </Form>
    </Popup>
  );
}
